import React, { createContext, useContext, useState, useEffect } from 'react';

const CookieContext = createContext();

export const useCookies = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error('useCookies debe ser usado dentro de un CookieProvider');
  }
  return context;
};

export const CookieProvider = ({ children }) => {
  const [cookieConsent, setCookieConsent] = useState(null);
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);

  // Configuración por defecto de cookies
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Siempre true, no se puede desactivar
    functional: false, // Google Fonts (técnicamente necesarias pero damos opción)
  });

  // Cargar preferencias guardadas al iniciar
  useEffect(() => {
    const savedConsent = localStorage.getItem('farmway-cookie-consent');
    const savedPreferences = localStorage.getItem('farmway-cookie-preferences');
    
    if (savedConsent) {
      const consentData = JSON.parse(savedConsent);
      setCookieConsent(consentData);
      
      if (savedPreferences) {
        setCookiePreferences(JSON.parse(savedPreferences));
      }
      
      // Si ya hay consentimiento, no mostrar banner
      setShowBanner(false);
    } else {
      // Si no hay consentimiento previo, mostrar banner después de 1 segundo
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, []);

  // Aplicar configuración de cookies
  useEffect(() => {
    if (cookieConsent) {
      applyGoogleFonts();
    }
  }, [cookieConsent, cookiePreferences]);

  const applyGoogleFonts = () => {
    // Si las cookies funcionales están deshabilitadas, usar fuentes del sistema
    if (!cookiePreferences.functional) {
      // Crear o actualizar estilo para usar fuentes del sistema
      let systemFontStyle = document.getElementById('system-fonts');
      if (!systemFontStyle) {
        systemFontStyle = document.createElement('style');
        systemFontStyle.id = 'system-fonts';
        document.head.appendChild(systemFontStyle);
      }
      
      systemFontStyle.textContent = `
        * {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', sans-serif !important;
        }
      `;
      
      // Remover enlaces de Google Fonts si existen
      const googleFontLinks = document.querySelectorAll('link[href*="fonts.googleapis.com"]');
      googleFontLinks.forEach(link => link.remove());
      
    } else {
      // Remover override de fuentes del sistema
      const systemFontStyle = document.getElementById('system-fonts');
      if (systemFontStyle) {
        systemFontStyle.remove();
      }
      
      // Asegurar que Google Fonts esté cargado si no existe
      if (!document.querySelector('link[href*="fonts.googleapis.com"]')) {
        const preconnect1 = document.createElement('link');
        preconnect1.rel = 'preconnect';
        preconnect1.href = 'https://fonts.googleapis.com';
        
        const preconnect2 = document.createElement('link');
        preconnect2.rel = 'preconnect';
        preconnect2.href = 'https://fonts.gstatic.com';
        preconnect2.crossOrigin = 'anonymous';
        
        const fontLink = document.createElement('link');
        fontLink.rel = 'stylesheet';
        fontLink.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap';
        
        document.head.appendChild(preconnect1);
        document.head.appendChild(preconnect2);
        document.head.appendChild(fontLink);
      }
    }
  };

  const acceptAllCookies = () => {
    const preferences = {
      necessary: true,
      functional: true,
    };
    
    const consent = {
      timestamp: new Date().toISOString(),
      version: '1.0',
      accepted: true,
      preferences: preferences
    };

    setCookiePreferences(preferences);
    setCookieConsent(consent);
    setShowBanner(false);
    
    // Guardar en localStorage
    localStorage.setItem('farmway-cookie-consent', JSON.stringify(consent));
    localStorage.setItem('farmway-cookie-preferences', JSON.stringify(preferences));
  };

  const rejectAllCookies = () => {
    const preferences = {
      necessary: true,
      functional: false,
    };
    
    const consent = {
      timestamp: new Date().toISOString(),
      version: '1.0',
      accepted: false,
      preferences: preferences
    };

    setCookiePreferences(preferences);
    setCookieConsent(consent);
    setShowBanner(false);
    
    // Guardar en localStorage
    localStorage.setItem('farmway-cookie-consent', JSON.stringify(consent));
    localStorage.setItem('farmway-cookie-preferences', JSON.stringify(preferences));
  };

  const saveCustomPreferences = (newPreferences) => {
    const consent = {
      timestamp: new Date().toISOString(),
      version: '1.0',
      accepted: true,
      preferences: newPreferences
    };

    setCookiePreferences(newPreferences);
    setCookieConsent(consent);
    setShowBanner(false);
    setShowPreferences(false);
    
    // Guardar en localStorage
    localStorage.setItem('farmway-cookie-consent', JSON.stringify(consent));
    localStorage.setItem('farmway-cookie-preferences', JSON.stringify(newPreferences));
  };

  const openPreferences = () => {
    setShowPreferences(true);
  };

  const resetCookieConsent = () => {
    localStorage.removeItem('farmway-cookie-consent');
    localStorage.removeItem('farmway-cookie-preferences');
    setCookieConsent(null);
    setCookiePreferences({
      necessary: true,
      functional: false,
    });
    setShowBanner(true);
  };

  const value = {
    cookieConsent,
    cookiePreferences,
    showBanner,
    showPreferences,
    setShowPreferences,
    acceptAllCookies,
    rejectAllCookies,
    saveCustomPreferences,
    openPreferences,
    resetCookieConsent,
  };

  return (
    <CookieContext.Provider value={value}>
      {children}
    </CookieContext.Provider>
  );
};